import { useState } from "react";
import { message } from "antd";
import { useLibraryCreativesContext } from "../../../../core/components/creatives/library/useLibraryCreativesContext";

export const useAssignAdUnits = afterAssignCallback => {
  const [assigning, setAssigning] = useState(false);
  const {
    updateAdGroupCreatives,
    refetchMedias,
    refetchAdGroupCreatives,
  } = useLibraryCreativesContext();

  const handleAssignAdUnits = async (adGroupIds, creativeIds) => {
    setAssigning(true);

    try {
      if (!Array.isArray(adGroupIds) || adGroupIds.length === 0) {
        message.error("Please select at least one Ad Group.");
        return;
      }
      if (!Array.isArray(creativeIds) || creativeIds.length === 0) {
        message.error("Please select at least one Creative.");
        return;
      }

      // Process assignments with individual error handling
      const assignmentPromises = adGroupIds.map(async adGroupId => {
        try {
          await updateAdGroupCreatives({
            variables: {
              id: adGroupId,
              data: {
                medias: {
                  connect: creativeIds.map(id => ({ id })),
                },
              },
            },
          });
          return { success: true, adGroupId };
        } catch (error) {
          return { success: false, adGroupId, error: error.message };
        }
      });

      const results = await Promise.all(assignmentPromises);

      // Check results
      const successful = results.filter(r => r.success);
      const failed = results.filter(r => !r.success);

      if (successful.length > 0) {
        message.success(
          `Creatives successfully assigned to ${successful.length} Ad Group(s)!`
        );
      }

      if (failed.length > 0) {
        message.error(
          `Failed to assign to ${failed.length} Ad Group(s). Please try again.`
        );
      }

      // Only call callback and refetch if at least one assignment succeeded
      if (successful.length > 0) {
        if (afterAssignCallback) {
          try {
            await afterAssignCallback();
          } catch (callbackError) {
            // Don't let callback errors break the flow
          }
        }

        try {
          await refetchMedias();
        } catch (refetchError) {
          // Don't let refetch errors break the flow
        }

        try {
          // Refresh ad group data so newly-assigned groups drop off the
          // "available" list and the assigned-groups table stays in sync.
          if (refetchAdGroupCreatives) {
            await refetchAdGroupCreatives();
          }
        } catch (refetchError) {
          // Don't let refetch errors break the flow
        }
      }
    } catch (error) {
      message.error("Failed to assign creatives. Please try again.");
    } finally {
      setAssigning(false);
    }
  };

  return { handleAssignAdUnits, assigning };
};
